import React, {
  useId,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import chevron_down from 'assets/icons/chevron_down.svg';
import { DialogProps, NewDataField } from './dialogInterface';
import { useTranslation } from 'react-i18next';
import { useModalDialog } from 'reusable/useModalDialog';
import FetchFailed from 'reusable/FetchFailed';

const AddDialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  confirmLabel,
  cancelLabel,
  fields,
  handleSubmit,
}) => {
  const { t } = useTranslation('device');
  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  // ADM-15。保存が終わるまで確定ボタンを押せなくする（続けて押すと同じ内容を2回送っていた）。
  // EV-130・EV-131（RV-129・RV-130）。送信中（送り直し・処理中の問い合わせ直しを含む）は、
  // 入力・キャンセル・閉じる（Escape を含む）も止める。送った内容と画面が食い違ったり、
  // 結果を受け取る前に閉じたりしないため。
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const closeUnlessSubmitting = () => {
    if (!submitting.current) onClose();
  };
  const dialogRef = useModalDialog(isOpen, closeUnlessSubmitting);
  // ADM-16。項目名（label）と入力欄を結び付ける（読み上げで何の欄か分かるように）。
  const fieldId = useId();
  const initializeData = useCallback(() => {
    return fields.reduce((acc, field) => {
      if (
        field.type === 'select' &&
        field.options &&
        field.options.length > 0
      ) {
        acc[field.name] = field.options[0].value ?? null;
      } else {
        acc[field.name] = null;
      }
      return acc;
    }, {} as NewDataField);
  }, [fields]);
  const [newData, setNewData] = useState<NewDataField>(initializeData());

  // EV-322。開いたときは初期値から始める。開いている間に欄が変わった（選択肢の読み直しなど）ときは、
  // 入れた値を残す（以前は欄が変わるたびに入力が消えた）。
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setNewData(initializeData());
    } else if (isOpen) {
      setNewData((current) => {
        const next = initializeData();
        Object.keys(next).forEach((name) => {
          if (current[name] !== undefined && current[name] !== null) next[name] = current[name];
        });
        return next;
      });
    }
    wasOpen.current = isOpen;
  }, [isOpen, initializeData]);

  if (!isOpen) return null;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (submitting.current) return;
    const { name, value } = e.target;
    setNewData({
      ...newData,
      [name]: value,
    });
  };

  const handleFormSubmit = async (e: FormEvent<Element>) => {
    e.preventDefault();

    const processedData = fields.reduce(
      (acc, field) => {
        acc[field.name] = newData[field.name] || null;
        return acc;
      },
      { ...newData }
    );

    // ADM-15。入力は開き直したときに初期化する（上の useEffect）。
    // 送信のあとに戻すと、失敗して開いたままのときに入力が消えていた。
    if (submitting.current) return;
    submitting.current = true;
    setIsSubmitting(true);
    try {
      await handleSubmit(e, processedData);
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {title}
                  </h3>
                  <button type="button"
                    onClick={closeUnlessSubmitting}
                    disabled={isSubmitting}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form onSubmit={handleFormSubmit}>
                  {/* RV-129。送信中は入力・キャンセル・確定をまとめて無効にする。 */}
                  <fieldset disabled={isSubmitting} className="min-w-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 m-4 sm:m-6">
                    {fields.map((field, index) => (
                      <div key={index}>
                        <label
                          className="block mb-1 text-sm font-regular text-neutral/100"
                          htmlFor={`${fieldId}-${index}`}
                        >
                          {field.label}{' '}
                          <span className="text-error">
                            {field.isRequired === true && '*'}
                          </span>
                        </label>
                        {field.type === 'select' ? (
                          <div className="relative">
                            <select
                              name={field.name}
                              id={`${fieldId}-${index}`}
                              className="w-full mt-1 p-2 border border-neutral/50 text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none"
                              required={field.isRequired}
                              // EV-333（DS-AC1）。注記（下の FetchFailed）を出す条件とそろえる。onRetry の無い欄で在りもしない id を指さない。
                              aria-describedby={field.fetchError && field.onRetry ? `${fieldId}-${index}-note` : undefined}
                              value={newData[field.name] || ''}
                              onChange={handleChange}
                            >
                              {field.options?.map((option) => (
                                <option value={option.value} id={option.id}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-neutral/30 pr-4 ">
                              <img src={chevron_down} alt="" />
                            </div>
                          </div>
                        ) : (
                          <input
                            type={field.type}
                            name={field.name}
                            id={`${fieldId}-${index}`}
                            className="w-full mt-1 p-2 border border-neutral/50 text-neutral/30 focus:outline-none focus:border-neutral/75 text-body-2 rounded-md h-12 px-4 appearance-none"
                            required={field.isRequired}
                            placeholder={
                              field.type === 'date' ? 'yyyy/mm/dd' : ''
                            }
                            value={newData[field.name] || ''}
                            onChange={handleChange}
                          />
                        )}
                        {field.fetchError && field.onRetry ? (
                          <FetchFailed
                            id={`${fieldId}-${index}-note`}
                            compact
                            error={field.fetchError}
                            onRetry={field.onRetry}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={closeUnlessSubmitting}
                    >
                      {cancelLabel}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      aria-busy={isSubmitting}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-60"
                    >
                      {confirmLabel}
                    </button>
                  </div>
                  </fieldset>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDialog;
